import {SpUiConfigService} from './sp-ui-config.service';
import {SpFile, SpRemoteFile} from "../components/inputs/interfaces";

export class SpImageService {
    public static getUrlOrBlobFromImage(image: SpFile | SpRemoteFile, size: SpImageSize | 'full' = 'full') {
        if (!image) return;
        if ('blob' in image) return image.blob;
        if (image.spec.type === 'fullUri')
            return image.path;
        return SpImageService.buildUrl(image.spec.type, size !== 'full' ? 'thumbs' : 'images', image.path, size === 'full' ? null : size);
    }

    // TODO: make components use fallback options from buildUrl where possible
    public static buildUrl(type: SpType, subType: SpSubType, entityUrl?: string, size?: SpImageSize, useFallbackPlaceholder = false, placeholderType = 0): string {
        if (!!entityUrl)
            return `${ SpUiConfigService.get('SP_FILE_HOST') }/${ subType }/${type}${ size ? `/${size}` : '' }/${ entityUrl }.${ (subType === 'markers') ? 'png' : 'jpg' }`;
        if (useFallbackPlaceholder)
            return SpImageService.getPlaceholderUrl(type, subType, placeholderType);
        return null;
    }

    public static preload(type: SpType, subType: SpSubType, entityUrl: string, size?: SpImageSize, abortController?: AbortController): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            if (!entityUrl)
                reject(new Error(`Entity url is empty!`));
            else {
                const img = new Image();
                const url = this.buildUrl(type, subType, entityUrl, size);
                img.addEventListener('load', e => resolve(img));
                img.addEventListener('error', e => {
                    reject(new Error(`Failed to load image's URL: ${url}`));
                });
                abortController?.signal.addEventListener('abort', () => {
                    img.src = '';
                });
                img.src = url;
            }
        });
    }

    public static getPlaceholderUrl(type?: SpType, subType?: SpSubType, typeNumber = 0): string {
        switch (type) {
            case 'activities':
            case 'spots':
            case 'messages':
                switch (subType) {
                    case 'markers':
                        return `${ SpUiConfigService.get('SP_TOPIC_ASSET_HOST') }/img/dummy-marker-192.png`;
                    default:
                        return `${ SpUiConfigService.get('SP_TOPIC_ASSET_HOST') }/img/dummy.jpg`;
                }
            case 'users':
                return `${ SpUiConfigService.get('SP_STATIC_ASSET_HOST') }/img/avatar/type-${typeNumber}.png`;
            default:
                return `${ SpUiConfigService.get('SP_STATIC_ASSET_HOST') }/img/spotteron-dummy.jpg`;
        }
    }

    public static getBadgeUrl(badge: string): string {
        return `${SpUiConfigService.get('SP_TOPIC_ASSET_HOST')}/img/badge/spot-count-${badge}.png`
    }

    public static getRoleUrl(roleName: string): string {
        return `${SpUiConfigService.get('SP_TOPIC_ASSET_HOST')}/img/role-${roleName.replace('_','-')}.png`
    }


    public static dataURLtoBlob(dataURL: string): Blob {
        // convert base64 to raw binary data held in a string
        // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
        let byteString = atob(dataURL.split(',')[1]);

        // separate out the mime component
        let mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];

        // write the bytes of the string to an ArrayBuffer
        let ab = new ArrayBuffer(byteString.length);
        let ia = new Uint8Array(ab);
        for (var i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        try {
            return new Blob([ab], {type: mimeString});
        } catch (e) {
            // The BlobBuilder API has been deprecated in favour of Blob, but older
            // browsers don't know about the Blob constructor
            // IE10 also supports BlobBuilder, but since the `Blob` constructor
            //  also works, there's no need to add `MSBlobBuilder`.
            let BlobBuilder = (window as any).WebKitBlobBuilder || (window as any).MozBlobBuilder;
            let bb = new BlobBuilder();
            bb.append(ab);
            return bb.getBlob(mimeString);
        }
    }

    public static async blobToBase64(blob): Promise<string> {
        return new Promise((resolve, _) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
        });
    }

    public static async getImagesDimensions(images: (string | Blob)[]) {
        const loadingImages: Promise<HTMLImageElement>[] = [];
        const tmpObjectUrls = [];
        for (let src of images) {
            loadingImages.push(
                new Promise((resolve, reject) => {
                    let img = new Image();
                    img.onload = () => resolve(img);
                    img.onerror = e => {
                        resolve(null);
                    };
                    let objectURL;
                    if (typeof src !== 'string') {
                        objectURL= URL.createObjectURL(src);
                        tmpObjectUrls.push(objectURL);
                    }
                    img.src = objectURL ?? src;
                })
            );

        }
        const loadedImages = await Promise.all(loadingImages);
        for (const tmpObjectUrl of tmpObjectUrls)
            URL.revokeObjectURL(tmpObjectUrl)
        return loadedImages.map(img => {
            return img ? {
                height: img.height,
                width: img.width
            } : null;
        });
    }

    public static async resizeImage(image: string | Blob, maxWidth: number, maxHeight: number): Promise<Blob> {
        if (typeof image === 'string') {
            image = await (await fetch(image)).blob();
        }
        return new Promise(async resolve => {
            const bitmap = await createImageBitmap(image);
            let width = bitmap.width;
            let height = bitmap.height;

            if (width > height) {
                if (width > maxWidth) {
                    height = height * (maxWidth / width);
                    width = maxWidth;
                }
            } else {
                if (height > maxHeight) {
                    width = width * (maxHeight / height);
                    height = maxHeight;
                }
            }
            let canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(bitmap, 0, 0, width, height);
            ctx.canvas.toBlob(blob => resolve(blob));
        });

    }
}

export type SpType = 'activities' | 'users' | 'spots' | 'messages' | 'fullUri';
export type SpImageSize = 'x96' | 'x200' | 'x550';
export type SpSubType = 'thumbs' | 'images' | 'markers';
